import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";
import { API_CONFIG } from "../config/apiConfig";
import { supabase } from "../../utils/supabaseClient";

class ApiClient {
  private static instance: ApiClient;
  private axiosInstance: AxiosInstance;

  private constructor() {
    this.axiosInstance = axios.create(API_CONFIG);
    this.setupInterceptors();
  }

  public static getInstance(): ApiClient {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient();
    }
    return ApiClient.instance;
  }

  private setupInterceptors(): void {
    this.axiosInstance.interceptors.request.use(
      async (config: InternalAxiosRequestConfig) => {
        // Let the browser set the correct multipart Content-Type (with boundary).
        // The global 'application/json' default would otherwise cause axios to
        // serialize FormData bodies into JSON, breaking multipart uploads.
        if (config.data instanceof FormData) {
          config.headers.setContentType(false);
        }

        // Get token from Supabase session

        const {
          data: { session },
        } = await supabase.auth.getSession();


        if (session?.access_token) {
          config.headers.Authorization = `Bearer ${session.access_token}`;
        }

        // Add X-Organization-Id header for tenant context
        // Skip if header is already set in config (e.g., admin endpoints bypass tenant context)
        if (!("X-Organization-Id" in config.headers)) {
          const selectedOrgStr = localStorage.getItem("papo_selected_organization");
          if (selectedOrgStr) {
            try {
              const selectedOrg = JSON.parse(selectedOrgStr);
              if (selectedOrg?.id) {
                config.headers["X-Organization-Id"] = selectedOrg.id;
              }
            } catch {
              // Invalid JSON in localStorage, skip header
            }
          }
        } else if (config.headers["X-Organization-Id"] === "") {
          delete config.headers["X-Organization-Id"];
        }

        return config;
      },
      (error: AxiosError) => {
        return Promise.reject(error);
      }
    );

    this.axiosInstance.interceptors.response.use(
      (response: AxiosResponse) => {
        return response.data;
      },
      async (error: AxiosError & { config?: InternalAxiosRequestConfig & { _retried?: boolean } }) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && originalRequest && !originalRequest._retried) {
          // Mark as retried to prevent infinite loops
          originalRequest._retried = true;

          try {
            // Attempt to refresh the Supabase session
            const { data: { session }, error: refreshError } = await supabase.auth.refreshSession();

            if (refreshError || !session?.access_token) {
              // Refresh failed — genuinely expired session, log out
              const currentPath = window.location.pathname;
              const returnUrl =
                currentPath !== "/login" ? `?returnTo=${encodeURIComponent(currentPath)}` : "";
              await supabase.auth.signOut();
              window.location.href = `/login${returnUrl}`;
              return Promise.reject(error);
            }

            // Refresh succeeded — retry the original request with the new token
            originalRequest.headers.Authorization = `Bearer ${session.access_token}`;
            return this.axiosInstance(originalRequest);
          } catch {
            // Unexpected error during refresh — log out as fallback
            const currentPath = window.location.pathname;
            const returnUrl =
              currentPath !== "/login" ? `?returnTo=${encodeURIComponent(currentPath)}` : "";
            await supabase.auth.signOut();
            window.location.href = `/login${returnUrl}`;
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );
  }

  public getAxiosInstance(): AxiosInstance {
    return this.axiosInstance;
  }
}

export const apiClient = ApiClient.getInstance().getAxiosInstance();

// Export as axiosClient for backward compatibility
export const axiosClient = apiClient;

// Helper functions for common API operations
export const api = {
  get: <T>(url: string, config = {}) => apiClient.get<T, T>(url, config),
  post: <T>(url: string, data = {}, config = {}) => apiClient.post<T, T>(url, data, config),
  put: <T>(url: string, data = {}, config = {}) => apiClient.put<T, T>(url, data, config),
  patch: <T>(url: string, data = {}, config = {}) => apiClient.patch<T, T>(url, data, config),
  delete: <T>(url: string, config = {}) => apiClient.delete<T, T>(url, config),
};
